import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useClientAuth } from '../contexts/ClientAuthContext';
import ClientAuthService from '../services/clientAuthService';
import ClientSidebar from './ClientSidebar';
import ClientDashboardHome from './ClientDashboardHome';
import ClientSettings from './ClientSettings';
import ClientInvoiceModal from './ClientInvoiceModal';
import ClientQuoteModal from './ClientQuoteModal';
import WalletBalance from './WalletBalance';
import PaymentLinkModal from './PaymentLinkModal';
import InvoicePaymentLinkModal from './InvoicePaymentLinkModal';
import {
  FileText,
  DollarSign,
  Clock,
  Download,
  Eye,
  AlertCircle,
  CheckCircle,
  XCircle,
  Calendar,
  Menu,
  Upload,
  Trash2
} from 'lucide-react';
import clientAuthService from '../services/clientAuthService';
import { PDFLink } from './InvoicePDF';

const ClientDashboard = () => {
  const { client, logout } = useClientAuth();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Determine active section from URL
  const getActiveSectionFromPath = () => {
    const path = location.pathname.split('/').pop();
    const validSections = ['dashboard', 'invoices', 'quotes', 'documents', 'wallet', 'settings'];
    return validSections.includes(path) ? path : 'dashboard';
  };
  
  const [activeSection, setActiveSection] = useState(getActiveSectionFromPath());
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [data, setData] = useState({
    invoices: [],
    quotes: [],
    documents: [],
    profile: null,
    permissions: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [selectedQuoteId, setSelectedQuoteId] = useState(null);
  const [showWalletTopUp, setShowWalletTopUp] = useState(false);
  const [walletTopUpAmount] = useState('50');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedInvoiceForPayment, setSelectedInvoiceForPayment] = useState(null);
  const [walletRefreshTrigger, setWalletRefreshTrigger] = useState(0);
  const [showUploadSection, setShowUploadSection] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  // Update active section when location changes
  useEffect(() => {
    setActiveSection(getActiveSectionFromPath());
  }, [location.pathname]);

  useEffect(() => {
    loadData();

    // Check screen size and set mobile state
    const checkScreenSize = () => {
      const isMobileScreen = window.innerWidth <= 1024;
      setIsMobile(isMobileScreen);
      setSidebarCollapsed(isMobileScreen);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    // Check for payment success/error in URL
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');
    const paymentMessage = urlParams.get('message');
    const redirectTab = urlParams.get('tab');
    const redirectFlowComplete = urlParams.get('redirect_flow_complete');

    // Handle tab switching from URL parameters
    if (redirectTab) {
      const targetSection = redirectTab === 'payments' ? 'settings' : redirectTab;
      navigate(`/${targetSection}`);
    }

    // Handle GoCardless redirect flow completion
    if (redirectFlowComplete === 'true') {
      navigate('/settings');
      // Show success message
      setTimeout(() => {
        alert('Direct Debit set up successfully! You can now use it for payments.');
      }, 1000);
    }

    if (paymentStatus === 'success' && paymentMessage) {
      // Payment was processed successfully, refresh data
      setWalletRefreshTrigger(prev => prev + 1);

      // Clean up URL parameters
      const url = new URL(window.location);
      url.searchParams.delete('payment');
      url.searchParams.delete('message');
      window.history.replaceState({}, '', url);

    } else if (paymentStatus === 'error' && paymentMessage) {
      setError(paymentMessage);

      // Clean up URL parameters
      const url = new URL(window.location);
      url.searchParams.delete('payment');
      url.searchParams.delete('message');
      window.history.replaceState({}, '', url);
    }

    // Clean up redirect parameters
    if (redirectTab || redirectFlowComplete) {
      const url = new URL(window.location);
      url.searchParams.delete('tab');
      url.searchParams.delete('redirect_flow_complete');
      window.history.replaceState({}, '', url);
    }

    // Cleanup resize listener
    return () => {
      window.removeEventListener('resize', checkScreenSize);
    };
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [invoicesRes, quotesRes, documentsRes, profileRes, permissionsRes] = await Promise.all([
        ClientAuthService.getInvoices(),
        ClientAuthService.getQuotes(),
        ClientAuthService.getDocuments(),
        ClientAuthService.getProfile(),
        clientAuthService.getUserPermissions()
      ]);

      setData({
        invoices: invoicesRes.data || [],
        quotes: quotesRes.data || [],
        documents: documentsRes.data || [],
        profile: profileRes.data || null,
        permissions: permissionsRes.data || []
      });
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
  };

  const handleSectionChange = (section) => {
    navigate(`/${section}`);
    // Auto-collapse sidebar on mobile when navigating
    if (isMobile) {
      setSidebarCollapsed(true);
    }
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleNavigate = (section) => {
    navigate(`/${section}`);
  };

  const handleProfileUpdate = async () => {
    await loadData();
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'paid': { class: 'bg-green-100 text-green-800', icon: CheckCircle, text: 'Paid' },
      'sent': { class: 'bg-yellow-100 text-yellow-800', icon: Clock, text: 'Sent' },
      'overdue': { class: 'bg-red-100 text-red-800', icon: AlertCircle, text: 'Overdue' },
      'partially-paid': { class: 'bg-blue-100 text-blue-800', icon: Clock, text: 'Partially Paid' },
      'payment-pending': { class: 'bg-blue-100 text-blue-800', icon: Clock, text: 'Payment Processing' },
      'pending': { class: 'bg-gray-100 text-gray-800', icon: Clock, text: 'Pending' },
      'accepted': { class: 'bg-green-100 text-green-800', icon: CheckCircle, text: 'Accepted' },
      'rejected': { class: 'bg-red-100 text-red-800', icon: XCircle, text: 'Rejected' },
      'converted': { class: 'bg-blue-100 text-blue-800', icon: CheckCircle, text: 'Converted' },
      'expired': { class: 'bg-gray-100 text-gray-800', icon: XCircle, text: 'Expired' }
    };

    const config = statusConfig[status] || { class: 'bg-gray-100 text-gray-800', icon: Clock, text: status };
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.class}`}>
        <Icon size={12} className="mr-1" />
        {config.text}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const handleViewInvoice = (invoiceId) => {
    setSelectedInvoiceId(invoiceId);
    setShowInvoiceModal(true);
  };

  const handleViewQuote = (quoteId) => {
    setSelectedQuoteId(quoteId);
    setShowQuoteModal(true);
  };

  const handleDownloadDocument = async (documentId) => {
    try {
      await ClientAuthService.downloadDocument(documentId);
    } catch (error) {
      alert('Failed to download document: ' + error.message);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    addPendingFiles(files);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    addPendingFiles(files);
  };

  const addPendingFiles = (files) => {
    const validFiles = files.filter(file => {
      // Check file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        alert(`File "${file.name}" is too large. Maximum size is 5MB.`);
        return false;
      }
      return true;
    });

    const newPendingFiles = validFiles.map(file => ({
      _id: Math.random().toString(36).substring(2, 9),
      file: file,
      label: file.name,
      description: '',
      is_confidential: false
    }));

    setPendingFiles(prev => [...prev, ...newPendingFiles]);
  };

  const updatePendingFile = (id, field, value) => {
    setPendingFiles(prev => 
      prev.map(pf => pf._id === id ? { ...pf, [field]: value } : pf)
    );
  };

  const removePendingFile = (id) => {
    setPendingFiles(prev => prev.filter(pf => pf._id !== id));
  };

  const handleUpload = async () => {
    if (pendingFiles.length === 0) {
      alert('Please select files to upload');
      return;
    }

    // Validate all files have labels
    const filesWithoutLabels = pendingFiles.filter(pf => !pf.label.trim());
    if (filesWithoutLabels.length > 0) {
      alert('Please provide labels for all files');
      return;
    }

    setUploading(true);
    const successCount = { success: 0, failed: 0 };

    for (const pendingFile of pendingFiles) {
      try {
        await ClientAuthService.uploadDocument(pendingFile.file, {
          label: pendingFile.label,
          description: pendingFile.description,
          // isConfidential: pendingFile.is_confidential
        });
        successCount.success++;
      } catch (error) {
        console.error(`Failed to upload ${pendingFile.label}:`, error);
        successCount.failed++;
      }
    }

    setUploading(false);
    
    if (successCount.success > 0) {
      alert(`Successfully uploaded ${successCount.success} file(s)${successCount.failed > 0 ? `, ${successCount.failed} failed` : ''}`);
      setPendingFiles([]);
      setShowUploadSection(false);
      // Refresh documents list
      loadData();
    } else {
      alert('All uploads failed. Please try again.');
    }
  };

  const getFileIcon = (file) => {
    const type = file.type;
    if (type?.includes('pdf')) return 'fa-file-pdf text-red-500';
    if (type?.includes('image')) return 'fa-file-image text-green-500';
    if (type?.includes('word') || type?.includes('document')) return 'fa-file-word text-blue-500';
    if (type?.includes('excel') || type?.includes('spreadsheet')) return 'fa-file-excel text-green-500';
    return 'fa-file text-gray-500';
  };

  const handleWalletTopUp = () => {
    setShowWalletTopUp(true);
  };

  const handleWalletTopUpSuccess = (amount) => {
    setWalletRefreshTrigger(prev => prev + 1);
    // Refresh dashboard data
    loadData();
  };

  const handlePayInvoice = (invoice) => {
    setSelectedInvoiceForPayment(invoice);
    setShowPaymentModal(true);
  };

  const handlePaymentSuccess = (result) => {
    setWalletRefreshTrigger(prev => prev + 1);
    // Refresh dashboard data
    loadData();
  };

  const handleApprove = async (quoteId) => {
    try {
      setLoading(true);
      await ClientAuthService.approveQuote(quoteId);

      // Update local state to reflect approval
      // setQuote(prev => ({ ...prev, status: 'accepted' }));

      alert('Quote approved successfully!');
    } catch (error) {
      alert('Failed to approve quote: ' + error.message);
    } finally {
      setLoading(false);
      loadData();
    }
  };

  const handleReject = async (quoteId) => {
    try {
      setLoading(true);
      await ClientAuthService.rejectQuote(quoteId);

      // Update local state to reflect rejection
      // setQuote(prev => ({ ...prev, status: 'rejected' }));

      alert('Quote rejected successfully!');
    } catch (error) {
      alert('Failed to reject quote: ' + error.message);
    } finally {
      setLoading(false);
      loadData();
    }
  };

  const isExpired = (validUntil) => {
    return new Date(validUntil) < new Date();
  };

  const canTakeAction = (quote) => {
    return quote.status === 'pending' && !isExpired(quote?.validUntil) && data.permissions?.acceptQuotes;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen">
      {/* Mobile Header */}
      {isMobile && (
        <div className="fixed top-0 left-0 right-0 bg-white border-b border-gray-200 z-30 h-14 flex items-center justify-between px-4">
          <button
            className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
            onClick={toggleSidebar}
          >
            <Menu size={20} />
          </button>
          <div className="flex-1 text-center">
            <h6 className="text-lg font-semibold text-gray-900">Client Portal</h6>
          </div>
          <div className="text-right">
            <span className="text-sm text-gray-600 truncate max-w-24">{client?.name}</span>
          </div>
        </div>
      )}

      {/* Sidebar Overlay for mobile */}
      {isMobile && !sidebarCollapsed && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setSidebarCollapsed(true)}
        ></div>
      )}

      {/* Sidebar */}
      <div
        className={`${isMobile
            ? `fixed top-0 left-0 h-full w-64 z-50 transform transition-transform duration-300 ease-in-out ${sidebarCollapsed ? '-translate-x-full' : 'translate-x-0'
            }`
            : `sticky top-0 h-screen ${sidebarCollapsed ? 'w-16' : 'w-64'} transition-all duration-300`
          }`}
      >
        <ClientSidebar
          activeSection={activeSection}
          onSectionChange={handleSectionChange}
          client={client}
          onLogout={handleLogout}
          collapsed={sidebarCollapsed}
          isMobile={isMobile}
          permissions={data.permissions}
        />
      </div>

      {/* Main Content */}
      <div className={`flex-1 bg-gray-50 min-w-0 ${isMobile ? 'pt-14' : ''}`}>
        <div className="p-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
              {error}
            </div>
          )}

          {/* Content based on active section */}
          {activeSection === 'dashboard' && (
            <ClientDashboardHome data={data} onNavigate={handleNavigate} />
          )}

          {activeSection === 'invoices' && (
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Invoices</h3>
              {data.invoices.length === 0 ? (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                  <div className="text-center py-12">
                    <FileText size={48} className="text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">No invoices found.</p>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                  <div className="p-6">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice #</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {data.invoices.map(invoice => (
                            <tr key={invoice._id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{invoice.number}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(invoice.date)}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(invoice.dueDate)}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(invoice.totalAmount)}</td>
                              <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(invoice.status)}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                <div className="flex space-x-2">
                                  <button
                                    className="inline-flex items-center px-3 py-1.5 border border-blue-300 shadow-sm text-xs font-medium rounded text-blue-700 bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                    onClick={() => handleViewInvoice(invoice._id)}
                                  >
                                    <Eye size={12} className="mr-1" />
                                    View
                                  </button>
                                  <PDFLink item={invoice} type='invoice' className='inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500' />
                                  {data.permissions.payInvoices && (
                                    invoice.amountDue > 0 && invoice.status === 'payment-pending' ? (
                                      <button
                                        className="inline-flex items-center px-3 py-1.5 border border-blue-300 shadow-sm text-xs font-medium rounded text-blue-700 bg-blue-100 cursor-not-allowed opacity-60"
                                        disabled
                                        title="Payment is being processed by GoCardless (typically takes 1-3 business days)"
                                      >
                                        <Clock size={12} className="mr-1" />
                                        Processing
                                      </button>
                                    ) : invoice.amountDue > 0 ? (
                                      <button
                                        className="inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                                        onClick={() => handlePayInvoice(invoice)}
                                      >
                                        <DollarSign size={12} className="mr-1" />
                                        Pay
                                      </button>
                                    ) : null
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeSection === 'quotes' && (
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Quotes</h3>
              {data.quotes.length === 0 ? (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                  <div className="text-center py-12">
                    <DollarSign size={48} className="text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">No quotes found.</p>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                  <div className="p-6">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quote #</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Valid Until</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {data.quotes.map(quote => (
                            <tr key={quote._id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{quote.number}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(quote.date)}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(quote.validUntil)}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(quote.totalAmount)}</td>
                              <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(quote.status)}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                <div className="flex space-x-2">
                                  <button
                                    className="inline-flex items-center px-3 py-1.5 border border-blue-300 shadow-sm text-xs font-medium rounded text-blue-700 bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                    onClick={() => handleViewQuote(quote._id)}
                                  >
                                    <Eye size={12} className="mr-1" />
                                    View
                                  </button>
                                  <PDFLink item={quote} type='quote' className='inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500' />
                                  {quote.status === 'pending' && canTakeAction(quote) && (
                                    <>
                                      <button
                                        className="inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                                        onClick={() => handleApprove(quote._id)}
                                      >
                                        <CheckCircle size={12} className="mr-1" />
                                        Accept
                                      </button>
                                      <button
                                        className="inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-xs font-medium rounded text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                        onClick={() => handleReject(quote._id)}
                                      >
                                        <XCircle size={12} className="mr-1" />
                                        Reject
                                      </button>
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeSection === 'documents' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900">Documents</h3>
                {data.permissions?.uploadDocuments && !showUploadSection && (
                  <button
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    onClick={() => setShowUploadSection(true)}
                  >
                    <Upload size={16} className="mr-2" />
                    Upload Documents
                  </button>
                )}
              </div>

              {/* Upload Section */}
              {showUploadSection && data.permissions?.uploadDocuments && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6 p-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Upload New Documents</h4>
                  
                  {/* Drag and Drop Zone */}
                  <div
                    className={`border-2 border-dashed rounded-lg p-8 text-center mb-6 cursor-pointer transition-colors ${
                      dragOver ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
                    }`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <Upload className="mx-auto h-12 w-12 text-gray-400 mb-3" />
                    <p className="text-sm font-medium text-gray-700 mb-1">
                      Drag and drop files here, or{' '}
                      <label className="text-blue-600 hover:text-blue-700 cursor-pointer">
                        click to browse
                        <input
                          type="file"
                          className="hidden"
                          onChange={handleFileSelect}
                          accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.txt,.csv"
                          multiple
                        />
                      </label>
                    </p>
                    <p className="text-xs text-gray-500">
                      Supports: PDF, Word, Excel, Images, Text files (max 5MB each)
                    </p>
                  </div>

                  {/* Pending Files List */}
                  {pendingFiles.length > 0 && (
                    <div className="mb-6">
                      <h5 className="text-sm font-medium text-gray-700 mb-3">Files to Upload ({pendingFiles.length})</h5>
                      <div className="space-y-3">
                        {pendingFiles.map(pf => (
                          <div key={pf._id} className="border border-gray-200 rounded-lg p-4">
                            <div className="flex items-start gap-3">
                              <i className={`fas ${getFileIcon(pf.file)} mt-1`}></i>
                              <div className="flex-1 space-y-2">
                                <input
                                  type="text"
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                  placeholder="Label *"
                                  value={pf.label}
                                  onChange={(e) => updatePendingFile(pf._id, 'label', e.target.value)}
                                />
                                <input
                                  type="text"
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                  placeholder="Description (optional)"
                                  value={pf.description}
                                  onChange={(e) => updatePendingFile(pf._id, 'description', e.target.value)}
                                />
                              </div>
                              <button
                                type="button"
                                className="text-red-500 hover:text-red-700 focus:outline-none"
                                onClick={() => removePendingFile(pf._id)}
                              >
                                <Trash2 size={18} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Upload Actions */}
                  <div className="flex justify-end gap-3">
                    <button
                      type="button"
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                      onClick={() => {
                        setShowUploadSection(false);
                        setPendingFiles([]);
                      }}
                      disabled={uploading}
                    >
                      Cancel
                    </button>
                    {pendingFiles.length > 0 && (
                      <button
                        type="button"
                        className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={handleUpload}
                        disabled={uploading}
                      >
                        {uploading ? 'Uploading...' : `Upload ${pendingFiles.length} File(s)`}
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Documents List */}
              {data.documents.length === 0 && !showUploadSection ? (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                  <div className="text-center py-12">
                    <Download size={48} className="text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">No documents found.</p>
                    {data.permissions?.uploadDocuments && (
                      <button
                        className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        onClick={() => setShowUploadSection(true)}
                      >
                        <Upload size={16} className="mr-2" />
                        Upload Your First Document
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {data.documents.map(doc => (
                    <div key={doc._id} className="bg-white rounded-lg shadow-sm border border-gray-200">
                      <div className="p-6">
                        <h6 className="text-lg font-semibold text-gray-900 mb-2">{doc.label}</h6>
                        <p className="text-sm text-gray-500 mb-3">{doc.description}</p>
                        <p className="text-xs text-gray-400 mb-4 flex items-center">
                          <Calendar size={12} className="mr-1" />
                          {formatDate(doc.createdAt)}
                        </p>
                        <button
                          className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                          onClick={() => handleDownloadDocument(doc._id)}
                        >
                          <Download size={14} className="mr-1" />
                          Download
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {data.permissions.accesseWallet &&
            activeSection === 'wallet' && (
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Wallet</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <WalletBalance
                      onTopUp={handleWalletTopUp}
                      onViewHistory={() => {/* TODO: Implement history modal */ }}
                      refreshTrigger={walletRefreshTrigger}
                    />
                  </div>
                  <div>
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                      <div className="px-6 py-4 border-b border-gray-200">
                        <h6 className="text-lg font-semibold text-gray-900">Quick Actions</h6>
                      </div>
                      <div className="p-6">
                        <div className="space-y-3">
                          <button
                            className="w-full flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            onClick={handleWalletTopUp}
                          >
                            <DollarSign size={16} className="mr-2" />
                            Top Up Wallet
                          </button>
                          <button
                            className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                            onClick={() => navigate('/invoices')}
                          >
                            <FileText size={16} className="mr-2" />
                            Pay Invoices
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )
          }

          {activeSection === 'settings' && (
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Settings</h3>
              <ClientSettings
                profile={data.profile}
                onProfileUpdate={handleProfileUpdate}
                permissions={data.permissions}
              />
            </div>
          )}
        </div>
      </div>

      {/* Invoice Modal */}
      {showInvoiceModal && (
        <ClientInvoiceModal
          invoiceId={selectedInvoiceId}
          onClose={() => {
            setShowInvoiceModal(false);
            setSelectedInvoiceId(null);
          }}
        />
      )}

      {/* Quote Modal */}
      {showQuoteModal && (
        <ClientQuoteModal
          quoteId={selectedQuoteId}
          onClose={() => {
            setShowQuoteModal(false);
            setSelectedQuoteId(null);
          }}
          permissions={data.permissions}
        />
      )}

      {/* Wallet Top Up Modal */}
      {showWalletTopUp && (
        <PaymentLinkModal
          type="wallet"
          amount={walletTopUpAmount}
          onClose={() => setShowWalletTopUp(false)}
          onSuccess={handleWalletTopUpSuccess}
        />
      )}

      {/* Invoice Payment Modal */}
      {showPaymentModal && selectedInvoiceForPayment && (
        <InvoicePaymentLinkModal
          invoice={selectedInvoiceForPayment}
          onClose={() => {
            setShowPaymentModal(false);
            setSelectedInvoiceForPayment(null);
          }}
          onSuccess={handlePaymentSuccess}
        />
      )}
    </div>
  );
};

export default ClientDashboard;